/* <image-with-tabs>
 * Tab switcher for sections/image-with-tabs.liquid.
 *
 * Markup contract:
 *   <image-with-tabs>
 *     ...
 *     <div role="tablist">
 *       <button role="tab" data-tab-trigger="<id>" aria-selected="..." aria-controls="...">...</button>
 *     </div>
 *     <div data-tab-panel="<id>" hidden?>...</div>   ← image panel
 *     <div role="tabpanel" data-tab-panel="<id>" hidden?>...</div>   ← content panel
 *   </image-with-tabs>
 *
 * On click of a tab the host shows every element matching
 * [data-tab-panel="<id>"] (so the image AND the content panel switch
 * together) and hides the others. Keyboard navigation: ArrowLeft /
 * ArrowRight cycle through tabs; Home / End jump to first / last;
 * matches the WAI-ARIA tabs pattern.
 *
 * Theme-editor support: `shopify:block:select` activates the tab
 * whose block the merchant is editing.
 */
class ImageWithTabs extends HTMLElement {
  connectedCallback() {
    // Accordion wiring is independent of the tab strip — a single-tab
    // section renders no [data-tab-trigger] buttons but still has an
    // accordion row on the product template.
    this.initAccordion();

    this.tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
      tab.addEventListener('keydown', this.onTabKey.bind(this));
    });

    this.initTabsDrag();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }
  }

  initTabsDrag() {
    const strip = this.querySelector('.image-with-tabs__tabs');
    if (!strip) return;

    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    strip.addEventListener('mousedown', (e) => {
      isDragging = true;
      hasDragged = false;
      startX = e.pageX - strip.getBoundingClientRect().left;
      scrollStart = strip.scrollLeft;
      strip.classList.add('is-dragging');
    });

    const stopDrag = () => {
      isDragging = false;
      strip.classList.remove('is-dragging');
    };

    strip.addEventListener('mouseleave', stopDrag);
    strip.addEventListener('mouseup', stopDrag);

    strip.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - strip.getBoundingClientRect().left;
      const delta = x - startX;
      if (Math.abs(delta) > 4) hasDragged = true;
      strip.scrollLeft = scrollStart - delta;
    });

    // Prevent click from firing on a drag release
    strip.addEventListener('click', (e) => {
      if (hasDragged) e.stopPropagation();
    }, true);
  }

  // Product template only: below 990px the tab strip is hidden and each
  // panel becomes an accordion row (see .image-with-tabs--product in CSS).
  // The collapse itself is CSS-driven off the header's aria-expanded, so
  // here we only flip that attribute. First panel starts open (set in Liquid).
  initAccordion() {
    this.accHeaders = Array.from(this.querySelectorAll('[data-acc-trigger]'));
    this.accHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        const expanded = header.getAttribute('aria-expanded') === 'true';
        header.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  onTabClick(event) {
    const id = event.currentTarget.dataset.tabTrigger;
    if (id) this.activate(id, { focusTab: false });
  }

  onTabKey(event) {
    const list = this.tabs;
    const current = event.currentTarget;
    const i = list.indexOf(current);
    if (i < 0) return;

    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = (i + 1) % list.length;
        break;
      case 'ArrowLeft':
        next = (i - 1 + list.length) % list.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = list.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const tab = list[next];
    this.activate(tab.dataset.tabTrigger, { focusTab: true });
  }

  onBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    if (this.tabs.some((t) => t.dataset.tabTrigger === blockId)) {
      this.activate(blockId, { focusTab: false });
    }
  }

  activate(id, { focusTab } = { focusTab: false }) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tabTrigger === id;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active) {
        if (focusTab) tab.focus();
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== id;
    });

    // The newly-visible panel may contain <expandable-text> elements
    // that couldn't measure themselves on initial connect (the panel
    // was hidden then). Nudge a resize so they pick up the real
    // bounding rect now that layout is live.
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }
  }
}

if (!customElements.get('image-with-tabs')) {
  customElements.define('image-with-tabs', ImageWithTabs);
}

// After the document-request contact form posts, Shopify redirects back to
// the product page and the success/error feedback renders inside the modal,
// which starts closed. The [data-document-request-reopen] marker is only
// rendered on that post-submit pass (see snippets/document-request-modal.liquid),
// so reopen the modal to surface it. <modal-dialog> re-parents itself to
// <body>, hence the document-level lookup.
const reopenDocumentRequestModal = () => {
  const marker = document.querySelector('[data-document-request-reopen]');
  const modal = marker?.closest('modal-dialog');
  if (typeof modal?.show === 'function') modal.show();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reopenDocumentRequestModal);
} else {
  reopenDocumentRequestModal();
}
